import { Controller } from "@hotwired/stimulus"

// Wraps flatpickr inputs (data-controller="date-format" lives on the <input> itself).
//
// Turbo caveat: flatpickr with altInput mutates the DOM — it flips the original
// input to type="hidden" and inserts a visible alt input after it. Turbo snapshots
// that mutated DOM, so on a back/forward restoration visit Stimulus reconnects the
// target, flatpickr runs again, and a second alt input appears (issue #2975).
// We destroy the instance before Turbo caches the page and defensively strip any
// stale alt input on connect.
export default class extends Controller {
  static targets = ["flatpickrTarget"];

  connect() {
    this.boundBeforeCache = this.destroyFlatpickr.bind(this);
    document.addEventListener('turbo:before-cache', this.boundBeforeCache);
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache);
    this.destroyFlatpickr();
  }

  flatpickrTargetConnected(e) {
    this.cleanupStaleFlatpickr(e);

    let altInputClass = e.dataset.altInputClass || 'form-control normal-input';
    altInputClass += ' flatpickr-alt-input';
    flatpickr(e, {
      dateFormat: 'd/m/Y',
      allowInput: true,
      altInput: true,
      altInputClass: altInputClass,
      altFormat: 'm/d/Y',
      clickOpens: e.dataset.clickOpens || false,
      disableMobile: true
    });

    const flatpickrAltInput = e.closest('.flatpickr-group')?.querySelector('.flatpickr-alt-input')
    if (flatpickrAltInput) flatpickrAltInput.addEventListener('input', this.formatDateInput);
  }

  formatDateInput(event) {
    let input = event.target.value.replace(/\D/g, '');
    let formattedDate = '';

    if (input.length > 2 && input.length <= 4) {
      formattedDate = input.substring(0, 2) + '/' + input.substring(2);
    } else if (input.length > 4) {
      formattedDate = input.substring(0, 2) + '/' + input.substring(2, 4) + '/' + input.substring(4, 8);
    } else {
      formattedDate = input;
    }

    event.target.value = formattedDate;
  }

  flatpickrSnoozeTargetConnected(e) {
    this.cleanupStaleFlatpickr(e);

    flatpickr(e, {
      altInput: true,
      altFormat: 'm/d/Y',
      dateFormat: 'Y-m-d',
      allowInput: true,
      minDate: 'today',
    });
  }

  flatpickrTargetDisconnected(e) {
    e._flatpickr?.destroy();
  }

  flatpickrSnoozeTargetDisconnected(e) {
    e._flatpickr?.destroy();
  }

  // Runs on turbo:before-cache so the snapshot holds a plain <input>; flatpickr
  // re-initializes cleanly when the snapshot is restored and the target reconnects.
  destroyFlatpickr() {
    this.element._flatpickr?.destroy();
  }

  // If a cached snapshot slipped through (e.g. cached before this fix shipped),
  // the input arrives already hidden with an orphaned alt input beside it.
  // flatpickr's own destroy() restores the input, but a restored snapshot has no
  // live instance, so undo the DOM mutation by hand.
  cleanupStaleFlatpickr(el) {
    if (el._flatpickr) {
      el._flatpickr.destroy();
      return;
    }

    if (el.type !== 'hidden' || !el.classList.contains('flatpickr-input')) return;

    const stale = el.nextElementSibling;
    if (stale && stale.tagName === 'INPUT' && stale.classList.contains('flatpickr-input')) {
      stale.remove();
    }
    el.type = 'text';
    el.classList.remove('flatpickr-input');
  }
}
